import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SingleOption } from '@atlassian/paginated-filter';

import { useAppUpdatesContext } from '@app/app/src/AppContext';
import {
  getProductConfigByKey,
  getProductType,
  type ProductKey,
} from '@app/app-updates-product-app';
import SentryLogger from '@app/sentry';

import type { Product } from '../types';

const APPS_ENDPOINT = '/gateway/api/public/app-updates/v1/apps';

const EMPTY_PRODUCTS: Product[] = [];

export type AppCapability = 'PUBLIC_APP_UPDATES' | 'RELEASE_TRACK';

export interface UseAvailableAppsResult {
  productOptions: SingleOption[];
  isLoading: boolean;
  error: Error | null;
}

export const useAvailableApps = (
  capability: AppCapability,
): UseAvailableAppsResult => {
  const { api } = useAppUpdatesContext();

  const query = useQuery<Product[], Error>({
    queryKey: [APPS_ENDPOINT, capability],
    queryFn: async () => {
      try {
        const response = await api.get<string[]>(APPS_ENDPOINT, {
          params: { capability },
        });

        const keys: string[] = Array.isArray(response.data)
          ? response.data
          : [];

        return keys
          .map((key) => {
            const config = getProductConfigByKey(key as ProductKey);
            const productType = getProductType(key as ProductKey);
            if (!config || !productType) {
              // Log unknown keys so the team knows to update PRODUCT_LOGO_MAP
              SentryLogger.warn(`Unknown product key from API: "${key}"`);
              return null;
            }
            return {
              id: productType.toUpperCase(),
              name: config.displayName,
              icon: config.icon,
            } as Product;
          })
          .filter((p): p is Product => p !== null)
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        SentryLogger.error(error, {
          extra: { action: 'fetch-available-apps' },
        });
        throw error;
      }
    },
  });

  const products = query.data ?? EMPTY_PRODUCTS;

  const productOptions = useMemo(
    () =>
      products.map(
        (product): SingleOption => ({
          label: product.name,
          value: product.id,
          optionType: 'option',
        }),
      ),
    [products],
  );

  return {
    productOptions,
    isLoading: query.isLoading,
    error: query.error,
  };
};
